import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getComponentTypeFromDraggableId } from "utils/dragAndDrop";

const TOP_LEVEL_TYPES = new Set(["wall", "ceiling", "expFloor", "basement", "crawlspace", "slab"]);

const isTopLevelDraggableId = (draggableId) => TOP_LEVEL_TYPES.has(getComponentTypeFromDraggableId(draggableId));

const isWindowDraggableId = (draggableId) => getComponentTypeFromDraggableId(draggableId) === "window";

/** Windows cannot be selected together with top-level components (wall, ceiling, etc.). */
const isCompatibleWithSelection = (draggableId, selectedIds) => {
    if (!selectedIds.length || selectedIds.includes(draggableId)) return true;

    const addingWindow = isWindowDraggableId(draggableId);
    const addingTopLevel = isTopLevelDraggableId(draggableId);
    const hasWindow = selectedIds.some(isWindowDraggableId);
    const hasTopLevel = selectedIds.some(isTopLevelDraggableId);

    if (addingWindow && hasTopLevel) return false;
    if (addingTopLevel && hasWindow) return false;

    return true;
};

const defaultValue = {
    selectedComponentIds: [],
    setSelectedComponentIds: () => {},
    toggleSelected: () => {},
    toggleStoreySelection: () => {},
    clearSelection: () => {},
    isSelected: () => false,
    isDirectlySelected: () => false,
    canSelect: () => true,
    areAllSelected: () => false,
    dragSelectionCount: 0,
    setDragSelectionCount: () => {},
};

const EnclosureSelectionContext = createContext(defaultValue);

export const EnclosureSelectionProvider = ({ children, value }) => {
    const [internalSelected, setInternalSelected] = useState([]);
    const [internalDragCount, setInternalDragCount] = useState(0);

    const selectedComponentIds = value?.selectedComponentIds ?? internalSelected;
    const setSelectedComponentIds = value?.setSelectedComponentIds ?? setInternalSelected;
    const dragSelectionCount = value?.dragSelectionCount ?? internalDragCount;
    const setDragSelectionCount = value?.setDragSelectionCount ?? setInternalDragCount;

    const toggleSelected = useCallback(
        (draggableId) => {
            setSelectedComponentIds((prev) => {
                if (prev.includes(draggableId)) {
                    return prev.filter((id) => id !== draggableId);
                }
                if (!isCompatibleWithSelection(draggableId, prev)) {
                    return prev;
                }
                return [...prev, draggableId];
            });
        },
        [setSelectedComponentIds]
    );

    const toggleStoreySelection = useCallback(
        (storeyDraggableIds = []) => {
            if (!storeyDraggableIds.length) return;

            setSelectedComponentIds((prev) => {
                const allSelected = storeyDraggableIds.every((id) => prev.includes(id));

                if (allSelected) {
                    return prev.filter((id) => !storeyDraggableIds.includes(id));
                }

                // Drop selections incompatible with the storey set (e.g. windows vs top-level)
                const compatibleExisting = prev.filter((existingId) =>
                    storeyDraggableIds.every((storeyId) => isCompatibleWithSelection(storeyId, [existingId]))
                );

                const withoutStorey = compatibleExisting.filter((id) => !storeyDraggableIds.includes(id));
                return [...withoutStorey, ...storeyDraggableIds];
            });
        },
        [setSelectedComponentIds]
    );

    const clearSelection = useCallback(() => {
        setSelectedComponentIds([]);
    }, [setSelectedComponentIds]);

    /** Exact membership in the selection array (not inherited from a parent). */
    const isDirectlySelected = useCallback(
        (draggableId) => selectedComponentIds.includes(draggableId),
        [selectedComponentIds]
    );

    /**
     * Visual selection: the component itself is selected, or an ancestor is
     * (e.g. a window under a selected wall).
     */
    const isSelected = useCallback(
        (draggableId) => {
            if (!draggableId) return false;
            if (selectedComponentIds.includes(draggableId)) return true;
            return selectedComponentIds.some((selectedId) =>
                draggableId.startsWith(`${selectedId}.subcomponents.`)
            );
        },
        [selectedComponentIds]
    );

    const canSelect = useCallback(
        (draggableId) => isCompatibleWithSelection(draggableId, selectedComponentIds),
        [selectedComponentIds]
    );

    const areAllSelected = useCallback(
        (draggableIds = []) =>
            draggableIds.length > 0 && draggableIds.every((id) => selectedComponentIds.includes(id)),
        [selectedComponentIds]
    );

    const contextValue = useMemo(
        () => ({
            selectedComponentIds,
            setSelectedComponentIds,
            toggleSelected,
            toggleStoreySelection,
            clearSelection,
            isSelected,
            isDirectlySelected,
            canSelect,
            areAllSelected,
            dragSelectionCount,
            setDragSelectionCount,
        }),
        [
            selectedComponentIds,
            setSelectedComponentIds,
            toggleSelected,
            toggleStoreySelection,
            clearSelection,
            isSelected,
            isDirectlySelected,
            canSelect,
            areAllSelected,
            dragSelectionCount,
            setDragSelectionCount,
        ]
    );

    return (
        <EnclosureSelectionContext.Provider value={contextValue}>{children}</EnclosureSelectionContext.Provider>
    );
};

export const useEnclosureSelection = () => useContext(EnclosureSelectionContext);

export default EnclosureSelectionContext;
